import {
  Ban,
  Check,
  CircleDot,
  CirclePause,
  Hand,
  type LucideIcon,
  Minus,
  RotateCcw,
  Square,
  Truck,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/utils/cn';

import type { RunStageProgress } from '../types/run.api.types';
import { catalogLabel } from '../utils/catalog-labels';

/** Soft badge tone per list status (the Batches list / detail): every status gets its own pill. */
const BADGE: Record<string, { tone: string; Icon: LucideIcon | null }> = {
  COMPLETED: { tone: 'bg-muted text-foreground', Icon: Check },
  FAILED: { tone: 'bg-error-50 text-error', Icon: X },
  REJECTED: { tone: 'bg-danger-50 text-danger', Icon: Ban },
  RUNNING: { tone: 'bg-info-50 text-info', Icon: null },
  DELIVERING: { tone: 'bg-info-50 text-info', Icon: Truck },
  PENDING: { tone: 'bg-info-50 text-info', Icon: CirclePause },
  AWAITING_GATE: { tone: 'bg-warning-50 text-warning', Icon: Hand },
  TERMINATED: { tone: 'bg-neutral-20 text-secondary-text', Icon: Square },
  RESET: { tone: 'bg-neutral-20 text-secondary-text', Icon: RotateCcw },
  SKIPPED: { tone: 'bg-neutral-20 text-secondary-text', Icon: Minus },
};

/**
 * A batch's status as a soft pill — icon + label (a pulsing dot while running, still under
 * reduced motion). Rejected (ban) and Failed (✕) never rest on colour alone.
 */
export function BatchStatusBadge({
  status,
  variant = 'pill',
  className,
}: {
  status: string;
  /** `icon`: just the status icon in its colour (label on hover and for screen readers) — for
   *  rows whose text already says the status, so the list doesn't repeat it as a pill. */
  variant?: 'pill' | 'icon';
  className?: string;
}) {
  const { t } = useTranslation('catalog-enrichment');
  const spec = BADGE[status] ?? { tone: 'bg-neutral-20 text-secondary-text', Icon: CircleDot };
  const { Icon } = spec;
  const label = catalogLabel(t, 'runStatus', status);

  if (variant === 'icon') {
    // Only the text colour of the tone — no pill fill.
    const text = spec.tone.split(' ').find((c) => c.startsWith('text-')) ?? 'text-secondary-text';
    return (
      <span
        title={label}
        className={cn('inline-flex size-4 shrink-0 items-center justify-center', text, className)}
      >
        {Icon ? (
          <Icon aria-hidden="true" className="size-4" />
        ) : (
          <span
            aria-hidden="true"
            className="size-2 rounded-full bg-current motion-safe:animate-pulse"
          />
        )}
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-sm font-semibold whitespace-nowrap',
        spec.tone,
        className,
      )}
    >
      {Icon ? (
        <Icon aria-hidden="true" className="size-3.5" />
      ) : (
        <span
          aria-hidden="true"
          className="size-2 rounded-full bg-current motion-safe:animate-pulse"
        />
      )}
      {label}
    </span>
  );
}

/** The current-step fill per status on the list track (the running step pulses). */
const TRACK_MARK: Record<string, string> = {
  FAILED: 'bg-error',
  REJECTED: 'bg-danger',
  TERMINATED: 'bg-secondary-text',
  AWAITING_GATE: 'bg-warning',
  RUNNING: 'bg-info motion-safe:animate-pulse',
  DELIVERING: 'bg-info motion-safe:animate-pulse',
  PENDING: 'bg-info/50',
  RESET: 'bg-info/50',
};

interface TrackProps {
  progress: RunStageProgress | null | undefined;
  status: string;
  /** `large` for the detail column and the review card. */
  size?: 'default' | 'large';
  /** Print "Step 3 of 10" / "10 of 10 steps" under the track. */
  caption?: boolean;
  /** The stage name to add to the caption ("Step 3 of 10: Matching"). */
  stageLabel?: string | null;
  /** False once a stop is over a week old: its mark reads grey, not red (the 7-day rule). */
  recent?: boolean;
  className?: string;
}

/**
 * The Batches step track: one rounded segment per step of the batch's own flow — finished steps in
 * soft success, the step it stopped at (or is on) in its status colour, steps ahead on the track
 * tone. Unlike `BatchStageRail` it also draws a completed batch (every segment done).
 */
export function BatchStepTrackBar({
  progress,
  status,
  size = 'default',
  caption = false,
  stageLabel,
  recent = true,
  className,
}: TrackProps) {
  const { t } = useTranslation('catalog-enrichment-runs');
  if (!progress || progress.total <= 0) return null;
  const total = progress.total;
  const complete = status === 'COMPLETED';
  const done = complete ? total : Math.min(Math.max(progress.done, 0), total);
  const step = Math.min(done + 1, total);
  const text = complete
    ? t('list.track.complete', { total })
    : stageLabel
      ? t('list.track.stepAt', { step, total, stage: stageLabel })
      : t('list.track.step', { step, total });

  return (
    <div className={cn('min-w-0', className)}>
      <div role="img" aria-label={text} title={text} className="flex gap-0.5">
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className={cn(
              'flex-1 rounded-full',
              size === 'large' ? 'h-2.5' : 'h-1.5',
              i < done
                ? 'bg-secondary-text/40'
                : i === done && TRACK_MARK[status]
                  ? !recent && (status === 'FAILED' || status === 'REJECTED')
                    ? 'bg-secondary-text'
                    : TRACK_MARK[status]
                  : 'bg-surface-progress-track',
            )}
          />
        ))}
      </div>
      {caption && (
        <p
          className={cn(
            'mt-1',
            size === 'large' ? 'text-secondary-text text-sm' : 'text-neutral-80 text-xs',
          )}
        >
          {text}
        </p>
      )}
    </div>
  );
}
